import {
  Filter,
  GlProgram,
  GpuProgram,
  Matrix,
  UniformGroup,
  type FilterSystem,
  type RenderSurface,
  type Sprite,
  type Texture,
} from 'pixi.js';

/**
 * Greys and dims a painting where a mask is white, in one pass: the world
 * maps' locked buildings. It goes on a copy of the map tiles drawn over the
 * real ones and draws only where the mask is (faded in by it), so the rest
 * of the painting shows through unchanged and a soft mask gives soft edges.
 *
 * The mask sprite is only read for where it sits; it must be on the stage
 * (it's never drawn).
 */
export class GreyVeilFilter extends Filter {
  private readonly mask: Sprite;

  constructor(mask: Sprite) {
    const uniforms = new UniformGroup({
      uMaskMatrix: { value: new Matrix(), type: 'mat3x3<f32>' },
      uGrey: { value: 0.85, type: 'f32' },
      uDim: { value: 0.6, type: 'f32' },
    });
    super({
      glProgram: GlProgram.from({ vertex: VERTEX, fragment: FRAGMENT, name: 'grey-veil-filter' }),
      gpuProgram: GpuProgram.from({
        vertex: { source: WGSL, entryPoint: 'mainVertex' },
        fragment: { source: WGSL, entryPoint: 'mainFragment' },
      }),
      resources: {
        veilUniforms: uniforms,
        uMaskTexture: mask.texture.source,
        uMaskSampler: mask.texture.source.style,
      },
    });
    this.mask = mask;
    mask.renderable = false;
  }

  /** How far the colour goes (0 none, 1 all grey). */
  set grey(v: number) {
    this.resources.veilUniforms.uniforms.uGrey = v;
  }

  /** How bright what's left is (1 as painted). */
  set dim(v: number) {
    this.resources.veilUniforms.uniforms.uDim = v;
  }

  override apply(
    filterManager: FilterSystem,
    input: Texture,
    output: RenderSurface,
    clearMode: boolean,
  ): void {
    const u = this.resources.veilUniforms.uniforms;
    filterManager.calculateSpriteMatrix(u.uMaskMatrix as Matrix, this.mask);
    filterManager.applyFilter(this, input, output, clearMode);
  }
}

const VERTEX = /* glsl */ `in vec2 aPosition;
out vec2 vTextureCoord;
out vec2 vMaskUv;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;
uniform mat3 uMaskMatrix;

vec4 filterVertexPosition(void)
{
    vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
    position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
    return vec4(position, 0.0, 1.0);
}

vec2 filterTextureCoord(void)
{
    return aPosition * (uOutputFrame.zw * uInputSize.zw);
}

void main(void)
{
    gl_Position = filterVertexPosition();
    vTextureCoord = filterTextureCoord();
    vMaskUv = (uMaskMatrix * vec3(vTextureCoord, 1.0)).xy;
}
`;

const FRAGMENT = /* glsl */ `in vec2 vTextureCoord;
in vec2 vMaskUv;
out vec4 finalColor;

uniform sampler2D uTexture;
uniform sampler2D uMaskTexture;
uniform float uGrey;
uniform float uDim;

void main()
{
    vec2 inside = step(vec2(0.0), vMaskUv) * step(vMaskUv, vec2(1.0));
    float m = texture(uMaskTexture, vMaskUv).r * inside.x * inside.y;
    vec4 c = texture(uTexture, vTextureCoord);
    float l = dot(c.rgb, vec3(0.299, 0.587, 0.114));
    // A touch cool, as stone in shade.
    vec3 g = mix(c.rgb, vec3(l) * vec3(0.96, 0.98, 1.04), uGrey) * uDim;
    finalColor = vec4(g, c.a) * m;
}
`;

const WGSL = /* wgsl */ `
struct GlobalFilterUniforms {
  uInputSize:vec4<f32>,
  uInputPixel:vec4<f32>,
  uInputClamp:vec4<f32>,
  uOutputFrame:vec4<f32>,
  uGlobalFrame:vec4<f32>,
  uOutputTexture:vec4<f32>,
};

struct VeilUniforms {
  uMaskMatrix:mat3x3<f32>,
  uGrey:f32,
  uDim:f32,
};

@group(0) @binding(0) var<uniform> gfu: GlobalFilterUniforms;
@group(0) @binding(1) var uTexture: texture_2d<f32>;
@group(0) @binding(2) var uSampler : sampler;

@group(1) @binding(0) var<uniform> veilUniforms : VeilUniforms;
@group(1) @binding(1) var uMaskTexture: texture_2d<f32>;
@group(1) @binding(2) var uMaskSampler : sampler;

struct VSOutput {
  @builtin(position) position: vec4<f32>,
  @location(0) uv : vec2<f32>,
  @location(1) maskUv : vec2<f32>,
};

fn filterVertexPosition(aPosition:vec2<f32>) -> vec4<f32>
{
  var position = aPosition * gfu.uOutputFrame.zw + gfu.uOutputFrame.xy;
  position.x = position.x * (2.0 / gfu.uOutputTexture.x) - 1.0;
  position.y = position.y * (2.0*gfu.uOutputTexture.z / gfu.uOutputTexture.y) - gfu.uOutputTexture.z;
  return vec4(position, 0.0, 1.0);
}

fn filterTextureCoord(aPosition:vec2<f32>) -> vec2<f32>
{
  return aPosition * (gfu.uOutputFrame.zw * gfu.uInputSize.zw);
}

@vertex
fn mainVertex(@location(0) aPosition : vec2<f32>) -> VSOutput {
  let uv = filterTextureCoord(aPosition);
  return VSOutput(
    filterVertexPosition(aPosition),
    uv,
    (veilUniforms.uMaskMatrix * vec3(uv, 1.0)).xy
  );
}

@fragment
fn mainFragment(
  @location(0) uv: vec2<f32>,
  @location(1) maskUv: vec2<f32>,
) -> @location(0) vec4<f32> {
  let inside = step(vec2(0.0), maskUv) * step(maskUv, vec2(1.0));
  let m = textureSample(uMaskTexture, uMaskSampler, maskUv).r * inside.x * inside.y;
  let c = textureSample(uTexture, uSampler, uv);
  let l = dot(c.rgb, vec3(0.299, 0.587, 0.114));
  let g = mix(c.rgb, vec3(l) * vec3(0.96, 0.98, 1.04), veilUniforms.uGrey) * veilUniforms.uDim;
  return vec4(g, c.a) * m;
}
`;
